import {
  buildCronExpression,
  buildRateExpression,
  DAY_NAME_TO_INDEX,
  DEFAULT_SCHEDULE_STATE,
  DEFAULT_SELECTED_DAYS,
  formatRateDisplay,
  RECURRENCE_TYPE,
  SCHEDULE_PRESET_OPTIONS,
  SCHEDULE_TYPE,
  TIME_UNIT,
  type RecurrenceType,
  type ScheduleAmPm,
  type ScheduleType,
  type TimeUnit,
} from "@/lib/schedule"
import { convertTo12Hour, convertTo24Hour } from "@/utils/date/timezone"

import {
  isValidScheduleHour12,
  isValidScheduleHour24,
  isValidScheduleMinute,
  normalizeScheduleRecurrenceType,
  normalizeScheduleSelectedDays,
  resolveScheduleTime12FromState,
  toScheduleInteger,
} from "./scheduleStateNormalization"

const RATE_EXPRESSION_REGEX = /^rate\((\d+)\s+([A-Za-z]+)\)$/
const CRON_EXPRESSION_REGEX = /^cron\((.+)\)$/
const CRON_PARTS_COUNT = 6
const HOUR_12_MIN = 1
const MINUTE_MIN = 0
const DEFAULT_FALLBACK_HOUR_24 = 9
const DEFAULT_FALLBACK_MINUTE = 0

const SCHEDULE_SUMMARY_TEXT = {
  intervalPrefix: "Runs every",
  intervalSuffix: "starting from when you save",
  dailyPrefix: "Runs daily at",
  weeklyPrefix: "Runs weekly on",
  weeklyTimeSeparator: " at ",
} as const

interface IntervalScheduleState {
  presetIndex: number | null
  customValue: string
  customUnit: TimeUnit
}

interface CronScheduleState {
  hour: string
  minute: string
  amPm: ScheduleAmPm
  recurrenceType: RecurrenceType
  selectedDays: Set<number>
}

interface ScheduleFormState
  extends IntervalScheduleState, Omit<CronScheduleState, "selectedDays"> {
  scheduleType: ScheduleType
  selectedDays: Set<number>
}

interface ParsedCronTimeState {
  hour: string
  minute: string
  amPm: ScheduleAmPm
}

const DEFAULT_INTERVAL_STATE: IntervalScheduleState = {
  presetIndex: DEFAULT_SCHEDULE_STATE.selectedPreset,
  customValue: DEFAULT_SCHEDULE_STATE.customValue,
  customUnit: DEFAULT_SCHEDULE_STATE.customUnit,
}

const DEFAULT_CRON_STATE_BASE = {
  hour: DEFAULT_SCHEDULE_STATE.scheduleHour,
  minute: DEFAULT_SCHEDULE_STATE.scheduleMinute,
  amPm: DEFAULT_SCHEDULE_STATE.scheduleAmPm,
  recurrenceType: DEFAULT_SCHEDULE_STATE.recurrenceType,
} as const

function createDefaultSelectedDays(): Set<number> {
  return new Set(DEFAULT_SELECTED_DAYS)
}

function getDefaultCronState(): CronScheduleState {
  return {
    ...DEFAULT_CRON_STATE_BASE,
    selectedDays: createDefaultSelectedDays(),
  }
}

function createCronStateWithParsedTime(
  parsedTime: ParsedCronTimeState,
  recurrenceType: RecurrenceType,
  selectedDays: Set<number>,
): CronScheduleState {
  return {
    ...parsedTime,
    recurrenceType,
    selectedDays,
  }
}

function getDefaultDailyCronExpression(): string {
  const defaultHour12 =
    toScheduleInteger(DEFAULT_SCHEDULE_STATE.scheduleHour) ?? HOUR_12_MIN
  const defaultMinute =
    toScheduleInteger(DEFAULT_SCHEDULE_STATE.scheduleMinute) ?? MINUTE_MIN
  const defaultTime24 = convertTo24Hour(
    `${defaultHour12}:${String(defaultMinute).padStart(2, "0")}`,
    DEFAULT_SCHEDULE_STATE.scheduleAmPm,
  )
  const [defaultHour24Part, defaultMinutePart] = defaultTime24.split(":")
  const defaultHour24 = toScheduleInteger(defaultHour24Part)
  const defaultMinute24 = toScheduleInteger(defaultMinutePart)

  if (
    defaultHour24 === null ||
    defaultMinute24 === null ||
    !isValidScheduleHour24(defaultHour24) ||
    !isValidScheduleMinute(defaultMinute24)
  ) {
    return buildCronExpression(
      DEFAULT_FALLBACK_HOUR_24,
      DEFAULT_FALLBACK_MINUTE,
      RECURRENCE_TYPE.DAILY,
      createDefaultSelectedDays(),
    )
  }

  return buildCronExpression(
    defaultHour24,
    defaultMinute24,
    RECURRENCE_TYPE.DAILY,
    createDefaultSelectedDays(),
  )
}

function normalizeRateUnit(unit: string): TimeUnit | null {
  const singularUnit = unit.toLowerCase().replace(/s$/, "")
  if (singularUnit === "minute") {
    return TIME_UNIT.MINUTES
  }
  if (singularUnit === "hour") {
    return TIME_UNIT.HOURS
  }
  if (singularUnit === "day") {
    return TIME_UNIT.DAYS
  }
  return null
}

function parseCronTimeState(
  cronHour: number,
  cronMinute: number,
): ParsedCronTimeState | null {
  const time24 = `${String(cronHour).padStart(2, "0")}:${String(cronMinute).padStart(2, "0")}`
  const { time, ampm } = convertTo12Hour(time24)
  const [hourPart] = time.split(":")
  const hour = toScheduleInteger(hourPart)

  if (hour === null || !isValidScheduleHour12(hour)) {
    return null
  }

  return {
    hour: String(hour),
    minute: String(cronMinute).padStart(2, "0"),
    amPm: ampm,
  }
}

function parseCronSelectedDays(dayOfWeek: string): Set<number> {
  return new Set(
    dayOfWeek
      .split(",")
      .map(
        (day) =>
          DAY_NAME_TO_INDEX[
            day.trim().toUpperCase() as keyof typeof DAY_NAME_TO_INDEX
          ],
      )
      .filter((dayIndex): dayIndex is number => dayIndex !== undefined),
  )
}

export function parseRateScheduleExpression(
  expression: string,
): IntervalScheduleState {
  const match = expression.trim().match(RATE_EXPRESSION_REGEX)
  if (!match) {
    return { ...DEFAULT_INTERVAL_STATE }
  }

  const value = toScheduleInteger(match[1])
  const unit = normalizeRateUnit(match[2])

  if (value === null || value < 1 || unit === null) {
    return { ...DEFAULT_INTERVAL_STATE }
  }

  const presetIndex = SCHEDULE_PRESET_OPTIONS.findIndex(
    (preset) => preset.value === value && preset.unit === unit,
  )

  return {
    presetIndex: presetIndex >= 0 ? presetIndex : null,
    customValue: String(value),
    customUnit: unit,
  }
}

export function parseCronScheduleExpression(
  expression: string,
): CronScheduleState {
  const match = expression.trim().match(CRON_EXPRESSION_REGEX)
  if (!match) {
    return getDefaultCronState()
  }

  const parts = match[1].trim().split(/\s+/)
  if (parts.length !== CRON_PARTS_COUNT) {
    return getDefaultCronState()
  }

  const cronMinute = toScheduleInteger(parts[0])
  const cronHour = toScheduleInteger(parts[1])
  const dayOfMonth = parts[2]
  const month = parts[3]
  const dayOfWeek = parts[4]
  const year = parts[5]

  if (
    cronMinute === null ||
    cronHour === null ||
    !isValidScheduleMinute(cronMinute) ||
    !isValidScheduleHour24(cronHour)
  ) {
    return getDefaultCronState()
  }

  const parsedTime = parseCronTimeState(cronHour, cronMinute)
  if (!parsedTime) {
    return getDefaultCronState()
  }

  const isSupportedDaily =
    month === "*" &&
    year === "*" &&
    ((dayOfMonth === "*" && dayOfWeek === "?") ||
      (dayOfMonth === "?" && dayOfWeek === "*"))
  const isSupportedWeekly = dayOfMonth === "?" && month === "*" && year === "*"

  if (!isSupportedDaily && !isSupportedWeekly) {
    if (dayOfWeek === "?" || dayOfWeek === "*") {
      return createCronStateWithParsedTime(
        parsedTime,
        RECURRENCE_TYPE.DAILY,
        createDefaultSelectedDays(),
      )
    }

    const selectedDays = parseCronSelectedDays(dayOfWeek)

    return createCronStateWithParsedTime(
      parsedTime,
      RECURRENCE_TYPE.WEEKLY,
      selectedDays.size > 0 ? selectedDays : createDefaultSelectedDays(),
    )
  }

  if (isSupportedDaily) {
    return createCronStateWithParsedTime(
      parsedTime,
      RECURRENCE_TYPE.DAILY,
      createDefaultSelectedDays(),
    )
  }

  const selectedDays = parseCronSelectedDays(dayOfWeek)

  if (selectedDays.size === 0) {
    return createCronStateWithParsedTime(
      parsedTime,
      RECURRENCE_TYPE.WEEKLY,
      createDefaultSelectedDays(),
    )
  }

  return createCronStateWithParsedTime(
    parsedTime,
    RECURRENCE_TYPE.WEEKLY,
    selectedDays,
  )
}

export function deriveScheduleFormState(expression: string): ScheduleFormState {
  const normalizedExpression = expression.trim()
  const intervalState = parseRateScheduleExpression(normalizedExpression)
  const cronState = parseCronScheduleExpression(normalizedExpression)

  if (normalizedExpression.startsWith("cron(")) {
    return {
      scheduleType: SCHEDULE_TYPE.TIME_OF_DAY,
      ...intervalState,
      ...cronState,
    }
  }

  return {
    scheduleType: SCHEDULE_TYPE.INTERVAL,
    ...intervalState,
    ...cronState,
  }
}

interface BuildScheduleExpressionInput {
  scheduleType: ScheduleType
  selectedPreset: number | null
  customValue: string
  customUnit: TimeUnit
  scheduleHour: string
  scheduleMinute: string
  scheduleAmPm: ScheduleAmPm
  recurrenceType: RecurrenceType
  selectedDays: Set<number>
}

export function buildScheduleExpressionFromState({
  scheduleType,
  selectedPreset,
  customValue,
  customUnit,
  scheduleHour,
  scheduleMinute,
  scheduleAmPm,
  recurrenceType,
  selectedDays,
}: BuildScheduleExpressionInput): string {
  if (scheduleType === SCHEDULE_TYPE.INTERVAL) {
    if (
      selectedPreset !== null &&
      selectedPreset >= 0 &&
      selectedPreset < SCHEDULE_PRESET_OPTIONS.length
    ) {
      const preset = SCHEDULE_PRESET_OPTIONS[selectedPreset]
      return buildRateExpression(preset.value, preset.unit)
    }

    const parsedValue = toScheduleInteger(customValue)
    const value = parsedValue !== null && parsedValue > 0 ? parsedValue : 1
    return buildRateExpression(value, customUnit)
  }

  const normalizedRecurrenceType =
    normalizeScheduleRecurrenceType(recurrenceType)
  const normalizedSelectedDays = normalizeScheduleSelectedDays(selectedDays)

  if (
    normalizedRecurrenceType === RECURRENCE_TYPE.WEEKLY &&
    normalizedSelectedDays.size === 0
  ) {
    return ""
  }

  const { hour12, minute, amPm } = resolveScheduleTime12FromState(
    scheduleHour,
    scheduleMinute,
    scheduleAmPm,
  )

  const time24 = convertTo24Hour(
    `${hour12}:${String(minute).padStart(2, "0")}`,
    amPm,
  )

  const [hour24Part, minutePart] = time24.split(":")
  const hour24 = toScheduleInteger(hour24Part)
  const minute24 = toScheduleInteger(minutePart)

  if (
    hour24 === null ||
    minute24 === null ||
    !isValidScheduleHour24(hour24) ||
    !isValidScheduleMinute(minute24)
  ) {
    return getDefaultDailyCronExpression()
  }

  return buildCronExpression(
    hour24,
    minute24,
    normalizedRecurrenceType,
    normalizedSelectedDays,
  )
}

const SUMMARY_DAY_LABELS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const

export function formatScheduleSummaryFromState(
  {
    scheduleType,
    selectedPreset,
    customValue,
    customUnit,
    scheduleHour,
    scheduleMinute,
    scheduleAmPm,
    recurrenceType,
    selectedDays,
  }: BuildScheduleExpressionInput,
  timezoneAbbreviation: string,
): string {
  if (scheduleType === SCHEDULE_TYPE.INTERVAL) {
    const preset =
      selectedPreset !== null &&
      selectedPreset >= 0 &&
      selectedPreset < SCHEDULE_PRESET_OPTIONS.length
        ? SCHEDULE_PRESET_OPTIONS[selectedPreset]
        : null
    const parsedValue = toScheduleInteger(customValue)
    const value = preset
      ? preset.value
      : parsedValue !== null && parsedValue > 0
        ? parsedValue
        : 1
    const unit = preset ? preset.unit : customUnit
    return `${SCHEDULE_SUMMARY_TEXT.intervalPrefix} ${formatRateDisplay(value, unit)}, ${SCHEDULE_SUMMARY_TEXT.intervalSuffix}`
  }

  const normalizedRecurrenceType =
    normalizeScheduleRecurrenceType(recurrenceType)
  const normalizedSelectedDays = normalizeScheduleSelectedDays(selectedDays)

  if (
    normalizedRecurrenceType === RECURRENCE_TYPE.WEEKLY &&
    normalizedSelectedDays.size === 0
  ) {
    return ""
  }

  const { hour12, minute, amPm } = resolveScheduleTime12FromState(
    scheduleHour,
    scheduleMinute,
    scheduleAmPm,
  )
  const time = `${hour12}:${String(minute).padStart(2, "0")} ${amPm} ${timezoneAbbreviation}`

  if (normalizedRecurrenceType === RECURRENCE_TYPE.DAILY) {
    return `${SCHEDULE_SUMMARY_TEXT.dailyPrefix} ${time}`
  }

  const dayLabels = Array.from(normalizedSelectedDays)
    .sort()
    .map((day) => SUMMARY_DAY_LABELS[day])
    .join(", ")
  return `${SCHEDULE_SUMMARY_TEXT.weeklyPrefix} ${dayLabels}${SCHEDULE_SUMMARY_TEXT.weeklyTimeSeparator}${time}`
}
